import { Search } from '@/DTOS/MentenancePlanning/Search';
import { EnvironmentOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useMemo, useState } from 'react';
import { Tooltip as LeafletTooltip, MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import { useAuth } from '../../../../context/AuthProvider';
import CompanyService from '../../../../services/company/CompanyService';
import MentenancePlanningService from '../../../../services/mentenancePlanning/MentenancePlanningService';
import UserService from '../../../../services/user/UserService';

type MaintenanceStatus = 1 | 2 | 3 | 5;

const STATUS_LABELS: Record<MaintenanceStatus, string> = {
  1: 'Netransmis',
  2: 'Transmis',
  3: 'A ajuns',
  5: 'Cerere reprogramare',
};

const STATUS_COLORS: Record<MaintenanceStatus, string> = {
  1: 'default',
  2: 'processing',
  3: 'success',
  5: 'warning',
};

const MAINTENANCE_STATUS_IDS: MaintenanceStatus[] = [1, 2, 3, 5];
const MAX_GEOCODED_LOCATIONS = 350;
const GEOCODE_CONCURRENCY = 6;
const COMPANY_PAGE_SIZE = 500;

const PAGE_SIZES = [10, 20, 30, 50];

interface MaintenanceRow {
  id: string;
  number?: string;
  statusId?: number;
  status?: { id?: number; name?: string } | null;
  periodicity?: string | null;
  dateEnd?: string | null;
  finalDate?: string | null;
  appointment?: string | null;
  intervalTimetable?: string | null;
  history?: string[] | null;
  contractId?: string | null;
  companyId?: string | null;
  workPlaceId?: string | null;
  assignedTechnicianId?: string | null;
  registeredByUserId?: string | null;
  interventionFileId?: string | null;
  metadata?: any;
  company?: any;
  workPlace?: any;
  assignedTechnician?: any;
  mentenancePlanningSystemTypes?: any[];
}

interface MarkerRow {
  markerKey: string;
  location: string;
  clientName: string;
  workPlaceName: string;
  addressText: string;
  total: number;
  statusBreakdown: Record<MaintenanceStatus, number>;
}

interface GeocodedMarker extends MarkerRow {
  lat: number;
  lon: number;
}

interface MapFilters {
  month: Dayjs;
  companies: string[];
  workPlace: string | null;
  periodicity: string | null;
  onlyUnassigned: boolean;
}

const GEOCODE_CACHE_KEY = 'maintenance_map_geocode_cache_v1';
const DEFAULT_MAP_CENTER: [number, number] = [45.9432, 24.9668];

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const normalizeAddressKey = (address: string) => address.trim().toLowerCase();

const loadGeocodeCache = (): Record<string, { lat: number; lon: number }> => {
  try {
    const rawValue = localStorage.getItem(GEOCODE_CACHE_KEY);
    if (!rawValue) {
      return {};
    }
    return JSON.parse(rawValue);
  } catch {
    return {};
  }
};

const saveGeocodeCache = (cache: Record<string, { lat: number; lon: number }>) => {
  try {
    localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
  } catch {}
};

const geocodeWithArcGis = async (address: string) => {
  const endpoint = `https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates?f=pjson&maxLocations=1&outFields=Match_addr&sourceCountry=ROU&SingleLine=${encodeURIComponent(address)}`;
  const response = await fetch(endpoint, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  const firstCandidate = data?.candidates?.[0];
  const lat = Number(firstCandidate?.location?.y);
  const lon = Number(firstCandidate?.location?.x);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  return { lat, lon };
};

const geocodeWithPhoton = async (address: string) => {
  const endpoint = `https://photon.komoot.io/api/?q=${encodeURIComponent(address)}&limit=1`;
  const response = await fetch(endpoint, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  const firstFeature = data?.features?.[0];
  const lon = Number(firstFeature?.geometry?.coordinates?.[0]);
  const lat = Number(firstFeature?.geometry?.coordinates?.[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  return { lat, lon };
};

const geocodeWithNominatim = async (address: string) => {
  const endpoint = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=ro&q=${encodeURIComponent(address)}`;
  const response = await fetch(endpoint, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'ro',
    },
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  if (!Array.isArray(data) || !data.length) {
    return null;
  }

  const firstResult = data[0];
  const lat = Number(firstResult?.lat);
  const lon = Number(firstResult?.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  return { lat, lon };
};

const geocodeAddress = async (address: string) => {
  const providers = [geocodeWithArcGis, geocodeWithPhoton, geocodeWithNominatim];

  for (const provider of providers) {
    try {
      const coordinates = await provider(address);
      if (coordinates) {
        return coordinates;
      }
    } catch {}
  }

  return null;
};

const FitMapToMarkers: React.FC<{ positions: [number, number][] }> = ({ positions }) => {
  const map = useMap();

  useEffect(() => {
    if (!positions.length) {
      map.setView(DEFAULT_MAP_CENTER, 7);
      return;
    }

    if (positions.length === 1) {
      map.setView(positions[0], 13);
      return;
    }

    const bounds = L.latLngBounds(positions.map((position) => L.latLng(position[0], position[1])));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [map, positions]);

  return null;
};

const getClientName = (record: MaintenanceRow) =>
  record?.metadata?.company?.name ?? record?.company?.name ?? 'Client necunoscut';

const getWorkPlaceName = (record: MaintenanceRow) =>
  record?.metadata?.workPlace?.name ?? record?.workPlace?.name ?? 'Punct necunoscut';

const getAddress = (record: MaintenanceRow) => {
  const workPlaceAddress = record?.metadata?.workPlace?.address ?? record?.workPlace?.address;
  const companyAddress = record?.metadata?.company?.address ?? record?.company?.address;
  const locality = record?.metadata?.company?.locality ?? record?.company?.locality;
  const baseAddress = workPlaceAddress ?? companyAddress;

  if (!baseAddress) {
    return '';
  }

  return [baseAddress, locality, 'Romania'].filter(Boolean).join(', ');
};

const getSystemNames = (record: MaintenanceRow) => {
  const systemNames = record?.mentenancePlanningSystemTypes
    ?.map((entry) => entry?.systemType?.name)
    ?.filter((name: string) => !!name);

  return systemNames && systemNames.length ? Array.from(new Set(systemNames)) : [];
};

const getMarkerKey = (record: MaintenanceRow) => {
  return [getClientName(record), getWorkPlaceName(record), getAddress(record)].join('||');
};

/** Generate a stable hex color from client name (same name => same color). */
const clientNameToColor = (name: string): string => {
  let hash = 0;
  const str = String(name || '');
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash % 360);
  const s = 65;
  const l = 48;
  const a = 1;
  const c = (1 - Math.abs(2 * (l / 100) - 1)) * (s / 100);
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l / 100 - c / 2;
  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const normalizeCompanyNames = (companies: string[]) =>
  Array.from(new Set(companies))
    .filter((company) => !!company)
    .sort((a, b) => a.localeCompare(b, 'ro', { sensitivity: 'base' }));

const areCoordinateMapsEqual = (
  left: Record<string, { lat: number; lon: number }>,
  right: Record<string, { lat: number; lon: number }>,
) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  for (const key of leftKeys) {
    if (!right[key]) return false;
    if (left[key].lat !== right[key].lat || left[key].lon !== right[key].lon) return false;
  }
  return true;
};

const areFiltersEqual = (left: MapFilters, right: MapFilters) => {
  const leftCompanies = normalizeCompanyNames(left.companies);
  const rightCompanies = normalizeCompanyNames(right.companies);

  return (
    left.month.isSame(right.month, 'month') &&
    left.workPlace === right.workPlace &&
    left.periodicity === right.periodicity &&
    left.onlyUnassigned === right.onlyUnassigned &&
    JSON.stringify(leftCompanies) === JSON.stringify(rightCompanies)
  );
};

const MentenancePlanningMapAssignment: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  const [draftFilters, setDraftFilters] = useState<MapFilters>({
    month: dayjs(),
    companies: [],
    workPlace: null,
    periodicity: null,
    onlyUnassigned: true,
  });
  const [appliedFilters, setAppliedFilters] = useState<MapFilters>({
    month: dayjs(),
    companies: [],
    workPlace: null,
    periodicity: null,
    onlyUnassigned: true,
  });
  const [selectedMarkerKey, setSelectedMarkerKey] = useState<string | null>(null);
  const [geocodedMarkerByKey, setGeocodedMarkerByKey] = useState<Record<string, { lat: number; lon: number }>>({});
  const [isGeocodingMarkers, setIsGeocodingMarkers] = useState<boolean>(false);
  const [selectedLegendClients, setSelectedLegendClients] = useState<string[]>([]);
  const [legendClientFilter, setLegendClientFilter] = useState<string>('');

  const [tableCurrentPage, setTableCurrentPage] = useState<number>(1);
  const [tablePageSize, setTablePageSize] = useState<number>(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkTechnicianId, setBulkTechnicianId] = useState<string | undefined>(undefined);
  const [bulkAssignDate, setBulkAssignDate] = useState<Dayjs | null>(null);

  const { data: companies = [] } = useQuery({
    queryKey: ['maintenanceMapCompanies'],
    queryFn: async () => {
      const allCompanies: any[] = [];
      let skip = 0;
      let totalItems = Number.POSITIVE_INFINITY;

      while (allCompanies.length < totalItems) {
        const data = await CompanyService.search({
          isActive: true,
          isWorkPlacesIncluded: true,
          skip,
          take: COMPANY_PAGE_SIZE,
        });

        const companiesBatch = data?.response ?? [];
        allCompanies.push(...companiesBatch);
        totalItems = typeof data?.totalItems === 'number' ? data.totalItems : allCompanies.length;

        if (!companiesBatch.length) {
          break;
        }

        skip += COMPANY_PAGE_SIZE;
      }

      const uniqueCompanies = new Map<string, any>();
      allCompanies.forEach((company: any) => {
        const companyName = company?.name;
        if (companyName && !uniqueCompanies.has(companyName)) {
          uniqueCompanies.set(companyName, company);
        }
      });

      return Array.from(uniqueCompanies.values()).sort((left: any, right: any) =>
        (left?.name ?? '').localeCompare(right?.name ?? '', 'ro', { sensitivity: 'base' }),
      );
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: technicians = [], isFetching: isTechniciansLoading } = useQuery({
    queryKey: ['maintenanceMapTechnicians'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: 'tehnician, senior_tehnician',
      }).then((data) => data?.response ?? []),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const appliedCompanyNames = useMemo(
    () => normalizeCompanyNames(appliedFilters.companies),
    [appliedFilters.companies],
  );
  const appliedCompaniesKey = useMemo(() => appliedCompanyNames.join('||'), [appliedCompanyNames]);

  const searchPayload: Search = useMemo(
    () => ({
      statusIds: MAINTENANCE_STATUS_IDS,
      isStatusIncluded: true,
      isCompanyIncluded: true,
      isWorkPlaceIncluded: true,
      isAssignedTechnicianIncluded: true,
      isMentenancePlanningSystemTypeIncluded: true,
      month: appliedFilters.month?.month() + 1,
      year: appliedFilters.month?.year(),
      companyName: appliedCompanyNames.length === 1 ? appliedCompanyNames[0] : undefined,
      workPlaceName: appliedFilters.workPlace || undefined,
      take: 2500,
      skip: 0,
      sortDirection: 'asc',
    }),
    [appliedFilters.month, appliedFilters.workPlace, appliedCompanyNames],
  );

  const {
    data: maintenancesResponse,
    isFetching: isMaintenancesLoading,
    refetch,
  } = useQuery({
    queryKey: [
      'maintenanceMapAssignment',
      appliedFilters.month?.format('YYYY-MM'),
      appliedCompaniesKey,
      appliedFilters.workPlace,
      appliedFilters.onlyUnassigned,
    ],
    queryFn: () => MentenancePlanningService.searchAll(searchPayload),
    refetchOnWindowFocus: false,
  });

  const maintenances: MaintenanceRow[] = maintenancesResponse?.response ?? [];

  const baseFilteredMaintenances = useMemo(() => {
    return maintenances.filter((record) => {
      if (appliedFilters.onlyUnassigned && record.assignedTechnicianId) {
        return false;
      }

      if (appliedCompanyNames.length > 0 && !appliedCompanyNames.includes(getClientName(record))) {
        return false;
      }

      if (appliedFilters.workPlace && getWorkPlaceName(record) !== appliedFilters.workPlace) {
        return false;
      }

      if (
        appliedFilters.periodicity != null &&
        appliedFilters.periodicity !== '' &&
        (record?.periodicity ?? '') !== appliedFilters.periodicity
      ) {
        return false;
      }

      return true;
    });
  }, [
    maintenances,
    appliedFilters.onlyUnassigned,
    appliedFilters.workPlace,
    appliedFilters.periodicity,
    appliedCompanyNames,
  ]);

  const filteredMaintenances = useMemo(() => {
    let result = baseFilteredMaintenances;

    if (selectedLegendClients.length > 0) {
      const clientSet = new Set(selectedLegendClients.map((c) => (c || '').trim()));
      result = result.filter((record) => clientSet.has((getClientName(record) || '').trim()));
    }

    if (selectedMarkerKey) {
      result = result.filter((record) => getMarkerKey(record) === selectedMarkerKey);
    }

    return result;
  }, [baseFilteredMaintenances, selectedLegendClients, selectedMarkerKey]);

  useEffect(() => {
    setTableCurrentPage((previous) => (previous === 1 ? previous : 1));
    setSelectedRowKeys((previous) => {
      const next = previous.filter((key) => filteredMaintenances.some((record) => record.id === key));
      if (next.length === previous.length && next.every((value, index) => value === previous[index])) {
        return previous;
      }
      return next;
    });
  }, [selectedLegendClients, filteredMaintenances]);

  const markerRows: MarkerRow[] = useMemo(() => {
    const rows = baseFilteredMaintenances;
    const markerMap = new Map<string, MarkerRow>();

    rows.forEach((record) => {
      const address = getAddress(record);
      if (!address) {
        return;
      }

      const markerKey = getMarkerKey(record);
      const statusId = record.statusId as MaintenanceStatus;
      const safeStatusId: MaintenanceStatus = MAINTENANCE_STATUS_IDS.includes(statusId) ? statusId : 1;

      if (!markerMap.has(markerKey)) {
        markerMap.set(markerKey, {
          markerKey,
          location: address,
          clientName: getClientName(record),
          workPlaceName: getWorkPlaceName(record),
          addressText: address,
          total: 0,
          statusBreakdown: { 1: 0, 2: 0, 3: 0, 5: 0 },
        });
      }

      const current = markerMap.get(markerKey)!;
      current.total += 1;
      current.statusBreakdown[safeStatusId] += 1;
    });

    return Array.from(markerMap.values()).sort((a, b) => b.total - a.total);
  }, [baseFilteredMaintenances]);

  const clientToColor = useMemo(() => {
    const clients = Array.from(new Set(markerRows.map((m) => m.clientName))).sort((a, b) =>
      a.localeCompare(b, 'ro', { sensitivity: 'base' }),
    );
    const map: Record<string, string> = {};
    clients.forEach((name) => {
      map[name] = clientNameToColor(name);
    });
    return map;
  }, [markerRows]);

  const maintenancesByMarkerKey = useMemo(() => {
    const map = new Map<string, MaintenanceRow[]>();
    baseFilteredMaintenances.forEach((record) => {
      const key = getMarkerKey(record);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(record);
    });
    return map;
  }, [baseFilteredMaintenances]);

  useEffect(() => {
    let isCancelled = false;

    if (!markerRows.length) {
      setGeocodedMarkerByKey((previous) => (Object.keys(previous).length ? {} : previous));
      setIsGeocodingMarkers(false);
      return;
    }

    const geocodeCache = loadGeocodeCache();
    const addressToMarkerKeys = new Map<string, string[]>();
    const addressToDisplay = new Map<string, string>();

    markerRows.forEach((marker) => {
      const addressKey = normalizeAddressKey(marker.location);
      addressToMarkerKeys.set(addressKey, [...(addressToMarkerKeys.get(addressKey) ?? []), marker.markerKey]);
      if (!addressToDisplay.has(addressKey)) {
        addressToDisplay.set(addressKey, marker.location);
      }
    });

    const initialGeocodedMap: Record<string, { lat: number; lon: number }> = {};
    const missingAddressKeys: string[] = [];

    addressToMarkerKeys.forEach((markerKeys, addressKey) => {
      const cachedCoordinates = geocodeCache[addressKey];
      if (cachedCoordinates) {
        markerKeys.forEach((markerKey) => {
          initialGeocodedMap[markerKey] = cachedCoordinates;
        });
      } else {
        missingAddressKeys.push(addressKey);
      }
    });

    setGeocodedMarkerByKey((previous) =>
      areCoordinateMapsEqual(previous, initialGeocodedMap) ? previous : initialGeocodedMap,
    );

    if (!missingAddressKeys.length) {
      setIsGeocodingMarkers(false);
      return;
    }

    setIsGeocodingMarkers(true);
    const queue = missingAddressKeys.slice(0, MAX_GEOCODED_LOCATIONS);
    let currentIndex = 0;

    const geocodeWorker = async () => {
      while (!isCancelled) {
        const nextIndex = currentIndex++;
        if (nextIndex >= queue.length) {
          break;
        }

        const addressKey = queue[nextIndex];
        const addressText = addressToDisplay.get(addressKey);
        if (!addressText) {
          continue;
        }

        const geocodedCoordinates = await geocodeAddress(addressText);
        if (!geocodedCoordinates || isCancelled) {
          continue;
        }

        geocodeCache[addressKey] = geocodedCoordinates;
        const relatedMarkerKeys = addressToMarkerKeys.get(addressKey) ?? [];

        setGeocodedMarkerByKey((previous) => {
          const updated = { ...previous };
          relatedMarkerKeys.forEach((markerKey) => {
            updated[markerKey] = geocodedCoordinates;
          });
          return updated;
        });
      }
    };

    Promise.all(Array.from({ length: Math.min(GEOCODE_CONCURRENCY, queue.length) }, () => geocodeWorker())).finally(
      () => {
        if (isCancelled) {
          return;
        }
        saveGeocodeCache(geocodeCache);
        setIsGeocodingMarkers(false);
      },
    );

    return () => {
      isCancelled = true;
    };
  }, [markerRows]);

  const geocodedMarkers = useMemo<GeocodedMarker[]>(() => {
    return markerRows
      .map((marker) => {
        const coordinates = geocodedMarkerByKey[marker.markerKey];
        if (!coordinates) {
          return null;
        }

        return {
          ...marker,
          lat: coordinates.lat,
          lon: coordinates.lon,
        };
      })
      .filter((marker): marker is GeocodedMarker => marker !== null);
  }, [markerRows, geocodedMarkerByKey]);

  const legendFilteredGeocodedMarkers = useMemo(() => {
    if (selectedLegendClients.length === 0) {
      return geocodedMarkers;
    }
    const set = new Set(selectedLegendClients);
    return geocodedMarkers.filter((m) => set.has(m.clientName));
  }, [geocodedMarkers, selectedLegendClients]);

  const visibleMarkers = useMemo(() => {
    const duplicateCounter = new Map<string, number>();

    return legendFilteredGeocodedMarkers.map((marker) => {
      const coordinateKey = `${marker.lat.toFixed(5)}|${marker.lon.toFixed(5)}`;
      const duplicateIndex = duplicateCounter.get(coordinateKey) ?? 0;
      duplicateCounter.set(coordinateKey, duplicateIndex + 1);

      if (duplicateIndex === 0) {
        return marker;
      }

      const angle = (duplicateIndex * 50 * Math.PI) / 180;
      const radius = 0.00035 * duplicateIndex;

      return {
        ...marker,
        lat: marker.lat + Math.sin(angle) * radius,
        lon: marker.lon + Math.cos(angle) * radius,
      };
    });
  }, [legendFilteredGeocodedMarkers]);

  const markerPositions = useMemo<[number, number][]>(
    () => visibleMarkers.map((marker) => [marker.lat, marker.lon]),
    [visibleMarkers],
  );

  const draftCompanyNames = useMemo(() => normalizeCompanyNames(draftFilters.companies), [draftFilters.companies]);

  const workPlaceOptions = useMemo(() => {
    if (!companies.length) {
      return [];
    }

    if (draftCompanyNames.length > 0) {
      const selectedCompanySet = new Set(draftCompanyNames);
      const selectedCompanyWorkPlaces = companies
        .filter((company: any) => selectedCompanySet.has(company?.name))
        .flatMap((company: any) => company?.workPlaces ?? []);

      const uniqueByName = new Map<string, any>();
      selectedCompanyWorkPlaces.forEach((workPlace: any) => {
        if (workPlace?.name && !uniqueByName.has(workPlace.name)) {
          uniqueByName.set(workPlace.name, workPlace);
        }
      });

      return Array.from(uniqueByName.values());
    }

    const workPlacesMap = new Map<string, any>();
    companies.forEach((company: any) => {
      (company?.workPlaces ?? []).forEach((workPlace: any) => {
        if (workPlace?.name && !workPlacesMap.has(workPlace.name)) {
          workPlacesMap.set(workPlace.name, workPlace);
        }
      });
    });

    return Array.from(workPlacesMap.values());
  }, [companies, draftCompanyNames]);

  const periodicityOptions = ['la cerere', 'lunar', 'trimestrial', 'semestrial', 'anual'];

  useEffect(() => {
    if (!draftFilters.workPlace) {
      return;
    }

    const stillAvailable = workPlaceOptions.some((workPlace: any) => workPlace?.name === draftFilters.workPlace);
    if (!stillAvailable) {
      setDraftFilters((previous) => ({ ...previous, workPlace: null }));
    }
  }, [draftFilters.workPlace, workPlaceOptions]);

  const handleApplyFilters = () => {
    const nextFilters: MapFilters = {
      ...draftFilters,
      month: draftFilters.month ?? dayjs(),
      companies: normalizeCompanyNames(draftFilters.companies),
    };

    const filtersChanged = !areFiltersEqual(nextFilters, appliedFilters);
    setSelectedMarkerKey(null);
    setSelectedRowKeys([]);
    setTableCurrentPage(1);

    if (!filtersChanged) {
      refetch();
      return;
    }

    setAppliedFilters(nextFilters);
  };

  const bulkAssignMutation = useMutation({
    mutationFn: async ({
      technicianId,
      ids,
      appointment,
    }: {
      technicianId: string;
      ids: string[];
      appointment?: string;
    }) => {
      const response = await MentenancePlanningService.bulkUpdate(
        ids.map((id) => ({ id, assignedTechnicianId: technicianId, ...(appointment && { appointment }) })),
      );
      if (response?.errors?.length) {
        throw new Error(response.errors.join(', '));
      }
      return response;
    },
    onSuccess: (_, { ids }) => {
      message.success(`${ids.length} mentenante au fost alocate tehnicianului selectat.`);
      queryClient.invalidateQueries({ queryKey: ['maintenanceMapAssignment'] });
      setSelectedRowKeys([]);
      setBulkTechnicianId(undefined);
      setBulkAssignDate(null);
    },
    onError: (error: any) => {
      message.error(error?.message ?? 'Nu s-a putut aloca tehnicianul pentru mentenantele selectate.');
    },
  });

  const updateMaintenanceMutation = useMutation({
    mutationFn: async ({ record, technicianId }: { record: MaintenanceRow; technicianId: string }) => {
      const companyId = record?.companyId ?? record?.company?.id ?? record?.metadata?.company?.id;
      const workPlaceId = record?.workPlaceId ?? record?.workPlace?.id ?? record?.metadata?.workPlace?.id;
      const systemTypes = record?.mentenancePlanningSystemTypes ?? [];
      const periodicity = record?.periodicity;
      const dateEnd = record?.dateEnd;

      if (!record?.id || !companyId || !workPlaceId || !periodicity || !dateEnd || !systemTypes.length) {
        throw new Error('Date insuficiente pentru actualizare mentenanta.');
      }

      const selectedTechnician = technicians.find((technician: any) => technician?.id === technicianId);
      const nextStatus = record?.statusId === 1 ? 2 : (record?.statusId ?? 2);
      const historyEntry = `[Alocare tehnician] - ${dayjs().format('DD-MM-YYYY HH:mm')}`;

      const payload = {
        id: record.id,
        number: record.number,
        contractId: record.contractId,
        statusId: nextStatus,
        status: record.status,
        companyId,
        company: record.company,
        workPlaceId,
        workPlace: record.workPlace,
        assignedTechnicianId: technicianId,
        assignedTechnician: selectedTechnician,
        registeredByUserId: record.registeredByUserId ?? profile?.id,
        interventionFileId: record.interventionFileId,
        finalDate: record.finalDate,
        periodicity,
        dateEnd,
        appointment: record.appointment,
        intervalTimetable: record.intervalTimetable,
        history: [...(record.history ?? []), historyEntry],
        mentenancePlanningSystemTypes: systemTypes.map((entry: any) => ({
          id: entry?.id,
          mentenancePlanningId: entry?.mentenancePlanningId ?? record.id,
          systemType: entry?.systemType,
          systemTypeId: entry?.systemTypeId ?? entry?.systemType?.id,
        })),
      };

      const response = await MentenancePlanningService.upsert(record.id, payload);
      if (response?.errors?.length) {
        throw new Error(response.errors.join(', '));
      }

      return response;
    },
    onSuccess: () => {
      message.success('Tehnicianul a fost alocat cu succes.');
      queryClient.invalidateQueries({ queryKey: ['maintenanceMapAssignment'] });
    },
    onError: (error: any) => {
      message.error(error?.message ?? 'Nu s-a putut aloca tehnicianul.');
    },
  });

  const handleBulkAssign = () => {
    const ids = selectedRowKeys.map(String).filter(Boolean);
    if (!ids.length) {
      message.warning('Selectati mentenante din tabela.');
      return;
    }
    if (!bulkTechnicianId) {
      message.warning('Selectati tehnicianul pentru alocare bulk.');
      return;
    }
    const appointment = bulkAssignDate ? bulkAssignDate.format('YYYY-MM-DD') : undefined;
    bulkAssignMutation.mutate({ technicianId: bulkTechnicianId, ids, appointment });
  };

  const tableColumns: any[] = [
    {
      title: 'Nr.',
      dataIndex: 'number',
      key: 'number',
      width: 90,
      render: (value: string) => <Tag color='blue'>{value ?? '-'}</Tag>,
    },
    {
      title: 'Client',
      key: 'company',
      render: (_: any, record: MaintenanceRow) => getClientName(record),
    },
    {
      title: 'Punct de lucru',
      key: 'workplace',
      render: (_: any, record: MaintenanceRow) => getWorkPlaceName(record),
    },
    {
      title: 'Periodicitate',
      dataIndex: 'periodicity',
      key: 'periodicity',
      width: 120,
      render: (value: string) => value ?? '-',
    },
    {
      title: 'Adresa',
      key: 'address',
      render: (_: any, record: MaintenanceRow) => {
        const address = getAddress(record);
        return address ? (
          <Tooltip title={address}>
            <Space>
              <EnvironmentOutlined />
              <span>{address}</span>
            </Space>
          </Tooltip>
        ) : (
          '-'
        );
      },
    },
    {
      title: 'Sisteme',
      key: 'systems',
      render: (_: any, record: MaintenanceRow) => {
        const systemNames = getSystemNames(record);
        return systemNames.length
          ? systemNames.map((name) => (
              <Tag key={`${record.id}-${name}`} color='geekblue'>
                {name}
              </Tag>
            ))
          : '-';
      },
    },
    {
      title: 'Termen',
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      width: 120,
      render: (value: string) => (value ? dayjs(value).format('DD-MM-YYYY') : '-'),
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      render: (_: any, record: MaintenanceRow) => {
        const rawStatus = record?.statusId as MaintenanceStatus;
        const statusId: MaintenanceStatus = MAINTENANCE_STATUS_IDS.includes(rawStatus) ? rawStatus : 1;
        return <Tag color={STATUS_COLORS[statusId]}>{STATUS_LABELS[statusId]}</Tag>;
      },
    },
  ];

  const isLoading = isMaintenancesLoading || isTechniciansLoading;

  if (isLoading && maintenances.length === 0) {
    return <Spin className='spinner' size='large' />;
  }

  return (
    <>
      <Card style={{ padding: 20, marginBottom: 20, boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}>
        <h2 style={{ marginBottom: 16 }}>Harta mentenante pentru alocare tehnicieni</h2>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <DatePicker
              value={draftFilters.month}
              picker='month'
              style={{ width: '100%' }}
              onChange={(value) => {
                setDraftFilters((previous) => ({ ...previous, month: value ?? dayjs() }));
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder='Filtru periodicitate'
              value={draftFilters.periodicity}
              allowClear
              showSearch
              optionFilterProp='label'
              style={{ width: '100%' }}
              onChange={(value) => {
                setDraftFilters((previous) => ({
                  ...previous,
                  periodicity: value ?? null,
                }));
              }}
              options={periodicityOptions.map((p) => ({ label: p, value: p }))}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Space size='large'>
              <Button type='primary' onClick={handleApplyFilters}>
                Aplica filtre
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  setSelectedMarkerKey(null);
                  setTableCurrentPage(1);
                  refetch();
                }}
              >
                Reincarca
              </Button>
              {selectedMarkerKey && (
                <Button
                  onClick={() => {
                    setSelectedMarkerKey(null);
                    setTableCurrentPage(1);
                  }}
                >
                  Sterge selectie locatie
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      <Card style={{ marginBottom: 20, boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}>
        <style>{`
          .client-marker-icon.leaflet-marker-icon { background: none !important; border: none !important; }
        `}</style>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={17}>
            {geocodedMarkers.length > 0 ? (
              <MapContainer
                {...({
                  center: DEFAULT_MAP_CENTER,
                  zoom: 7,
                  style: { width: '100%', height: 550, borderRadius: 8, overflow: 'hidden' },
                  scrollWheelZoom: true,
                } as React.ComponentProps<typeof MapContainer>)}
              >
                <TileLayer
                  {...({
                    attribution: '&copy; OpenStreetMap contributors',
                    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                  } as React.ComponentProps<typeof TileLayer>)}
                />
                <FitMapToMarkers positions={markerPositions} />
                {visibleMarkers.map((marker) => {
                  const markerColor = clientToColor[marker.clientName] ?? '#1890ff';
                  const icon = L.divIcon({
                    className: 'client-marker-icon',
                    html: `<span style="display:block;width:24px;height:24px;border-radius:50%;background:${markerColor};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);box-sizing:border-box;"></span>`,
                    iconSize: [24, 24],
                    iconAnchor: [12, 12],
                  });
                  const maintenancesAtMarker = maintenancesByMarkerKey.get(marker.markerKey) ?? [];
                  return (
                    <Marker
                      key={marker.markerKey}
                      position={[marker.lat, marker.lon]}
                      {...({ icon } as unknown as React.ComponentProps<typeof Marker>)}
                      eventHandlers={{
                        click: () => {
                          setSelectedLegendClients([marker.clientName]);
                          setSelectedMarkerKey(null);
                          setTableCurrentPage(1);
                        },
                      }}
                    >
                      <LeafletTooltip>
                        <div style={{ minWidth: 280, maxWidth: 360, maxHeight: 320, overflow: 'auto' }}>
                          <div style={{ marginBottom: 8 }}>
                            <strong>{marker.clientName}</strong>
                            <br />
                            <span>{marker.workPlaceName}</span>
                            <br />
                            <span style={{ fontSize: 12, color: '#666' }}>{marker.addressText}</span>
                            <br />
                          </div>
                          <div style={{ borderTop: '1px solid #eee', paddingTop: 6, marginTop: 6 }}>
                            {maintenancesAtMarker.slice(0, 15).map((m) => (
                              <div
                                key={m.id}
                                style={{
                                  fontSize: 12,
                                  marginBottom: 6,
                                  padding: 4,
                                  background: '#fafafa',
                                  borderRadius: 4,
                                }}
                              >
                                <strong>Nr. {m.number ?? '-'}</strong>
                                <br />
                                Periodicitate: {m.periodicity ?? '-'} · Termen:{' '}
                                {m.dateEnd ? dayjs(m.dateEnd).format('DD-MM-YYYY') : '-'}
                                <br />
                                Status: {STATUS_LABELS[(m.statusId as MaintenanceStatus) ?? 1]}
                                {getSystemNames(m).length ? ` · ${getSystemNames(m).join(', ')}` : ''}
                              </div>
                            ))}
                            {maintenancesAtMarker.length > 15 && (
                              <div style={{ fontSize: 11, color: '#888' }}>
                                + încă {maintenancesAtMarker.length - 15} mentenante
                              </div>
                            )}
                          </div>
                        </div>
                      </LeafletTooltip>
                      <Popup>
                        <div style={{ minWidth: 260 }}>
                          <strong>{marker.clientName}</strong>
                          <br />
                          <span>{marker.workPlaceName}</span>
                          <br />
                          <span>{marker.addressText}</span>
                          <br />
                          <strong>Total: {marker.total}</strong>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            ) : markerRows.length > 0 && isGeocodingMarkers ? (
              <div style={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin size='large' />
              </div>
            ) : markerRows.length > 0 ? (
              <div style={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Nu s-au putut geocoda adresele punctelor de lucru.
              </div>
            ) : (
              <div style={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Nu exista adrese disponibile pentru afisare pe harta.
              </div>
            )}
          </Col>
          <Col xs={24} lg={7}>
            <Card size='small' title='Legendă clienți' style={{ height: '100%', minHeight: 460 }}>
              <p style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                Selectați clienți pentru a afișa doar mentenantele lor pe hartă. Fără selecție = toți clienții.
              </p>
              <Input
                placeholder='Caută client...'
                allowClear
                value={legendClientFilter}
                onChange={(e) => setLegendClientFilter(e.target.value)}
                style={{ marginBottom: 8 }}
              />
              <Space style={{ marginBottom: 8 }}>
                <Button
                  type='link'
                  size='small'
                  onClick={() =>
                    setSelectedLegendClients(
                      Object.keys(clientToColor).sort((a, b) => a.localeCompare(b, 'ro', { sensitivity: 'base' })),
                    )
                  }
                >
                  Selectează toți
                </Button>
                <Button type='link' size='small' onClick={() => setSelectedLegendClients([])}>
                  Deselectează toți
                </Button>
              </Space>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  maxHeight: 320,
                  overflowY: 'auto',
                  paddingRight: 4,
                }}
              >
                {Object.entries(clientToColor)
                  .filter(
                    ([clientName]) =>
                      !legendClientFilter.trim() ||
                      clientName.toLowerCase().includes(legendClientFilter.trim().toLowerCase()),
                  )
                  .sort(([a], [b]) => a.localeCompare(b, 'ro', { sensitivity: 'base' }))
                  .map(([clientName, color]) => (
                    <div
                      key={clientName}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 0',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <Checkbox
                        checked={selectedLegendClients.includes(clientName)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLegendClients((prev) => [...prev, clientName]);
                          } else {
                            setSelectedLegendClients((prev) => prev.filter((c) => c !== clientName));
                          }
                        }}
                      />
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          backgroundColor: color,
                          border: '1px solid #fff',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                        title={clientName}
                      >
                        {clientName}
                      </span>
                    </div>
                  ))}
              </div>
              {geocodedMarkers.length > 0 && (
                <p style={{ marginTop: 12, marginBottom: 0, fontSize: 12, color: '#888' }}>
                  Locatii afișate pe hartă: {visibleMarkers.length}
                  {selectedLegendClients.length > 0 && ` (din ${geocodedMarkers.length})`}
                </p>
              )}
            </Card>
          </Col>
        </Row>
      </Card>

      {selectedRowKeys.length > 0 && (
        <Card size='small' style={{ marginBottom: 16 }}>
          <Space wrap align='center'>
            <span>Alocare bulk ({selectedRowKeys.length} selectate):</span>
            <DatePicker
              placeholder='Data alocare'
              value={bulkAssignDate}
              onChange={(value) => setBulkAssignDate(value ?? null)}
              style={{ width: 160 }}
            />
            <Select
              placeholder='Selectati tehnicianul'
              value={bulkTechnicianId}
              onChange={setBulkTechnicianId}
              style={{ minWidth: 200 }}
              allowClear
              showSearch
              optionFilterProp='label'
              filterOption={(input, option) =>
                `${option?.label ?? ''}`.toLowerCase().includes(input.trim().toLowerCase())
              }
              options={(technicians ?? []).map((t: any) => ({ label: t?.name ?? t?.id, value: t?.id }))}
            />
            <Button
              type='primary'
              onClick={handleBulkAssign}
              loading={bulkAssignMutation.isPending}
              disabled={!bulkTechnicianId}
            >
              Aloca selectate
            </Button>
            <Button
              onClick={() => {
                setSelectedRowKeys([]);
                setBulkTechnicianId(undefined);
                setBulkAssignDate(null);
              }}
            >
              Anuleaza selectie
            </Button>
          </Space>
        </Card>
      )}

      <Table
        rowKey='id'
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        columns={tableColumns}
        dataSource={filteredMaintenances}
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        locale={{
          emptyText: selectedMarkerKey
            ? 'Nu exista mentenante pentru locatia selectata.'
            : selectedLegendClients.length > 0
              ? 'Nu exista mentenante pentru clientii selectati din legenda.'
              : 'Nu exista mentenante pentru filtrele selectate.',
        }}
        pagination={{
          current: tableCurrentPage,
          pageSize: tablePageSize,
          total: filteredMaintenances.length,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZES.map((size) => size.toString()),
          onChange: (page, pageSize) => {
            setTableCurrentPage(page);
            setTablePageSize(pageSize);
          },
          onShowSizeChange: (_page, pageSize) => {
            setTableCurrentPage(1);
            setTablePageSize(pageSize);
          },
        }}
      />
    </>
  );
};

export default MentenancePlanningMapAssignment;
